import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchData } from '@framework/api/apiCalls';
import { api } from '@framework/constants/endpoints';
import { STALE_TIME } from '@framework/constants/query';
import { ServerStateKeys } from '@framework/constants/serverStateKeys';

import { flattenTopics } from '@meetings/details/agenda/agenda.service';
import { NEW_MEETING_UUID } from '@meetings/details/details.constants';
import { appendZonedTimes } from '@meetings/details/store/details.service';
import { type Meeting } from '@meetings/details/types';

import { type ApiResponse } from '@type/api';

import type { AxiosError, AxiosResponse } from 'axios';

type Options = {
    enabled?: boolean;
    includeInactiveMembers?: boolean;
    mode?: string;
    compact?: boolean;
    staleTime?: number;
};

export const fetchMeeting = async (meetingId: string | undefined, options?: Options) => {
    if (!meetingId) {
        throw new Error('Meeting ID is required');
    }
    const response: AxiosResponse<ApiResponse<Meeting>> = await fetchData(
        api.meetingDetails(meetingId, options)
    );
    return {
        ...response.data.results,
        // `select` is called every render anywhere useMeeting is called.
        // `flattenTopics` is expensive so we only want to call it once.
        flatTopics: flattenTopics(response.data.results.topics ?? [])
    };
};

const useMeeting = (
    meetingId: string | undefined,
    options?: Options
): UseQueryResult<Meeting, AxiosError<any>> => {
    return useQuery({
        queryKey: [ServerStateKeys.Meeting, meetingId, { mode: options?.mode }],
        queryFn: () => fetchMeeting(meetingId, { includeInactiveMembers: true, ...options }),
        enabled: Boolean(meetingId) && meetingId !== NEW_MEETING_UUID && options?.enabled,
        staleTime: options?.staleTime || STALE_TIME,
        select: (meeting: Meeting) => appendZonedTimes(meeting)
    });
};

export default useMeeting;
